/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface CreateParams {
  data: __model.InformacioSecundaria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportSecondaryInfoParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportSecondaryInfoParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this informacio secundaria. */
  codi_informacio: string;
}

export interface UpdateParams {
  data: __model.InformacioSecundaria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this informacio secundaria. */
  codi_informacio: string;
}

export interface PartialUpdateParams {
  data: __model.InformacioSecundaria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this informacio secundaria. */
  codi_informacio: string;
}

export interface DeleteParams {
  /** A unique value identifying this informacio secundaria. */
  codi_informacio: string;
}

@Injectable()
export class InformacioSecundariaService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  /** Gets a list of attribute categories */
  list(params: ListParams, multipart = false): Observable<__model.InformacioSecundariaList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.InformacioSecundariaList>(this.apiConfigService.options.apiUrl + `/informacio_secundaria/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.InformacioSecundaria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.InformacioSecundaria>(this.apiConfigService.options.apiUrl + `/informacio_secundaria/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Endpoint to export secondary info to CSV */
  exportSecondaryInfo(params: ExportSecondaryInfoParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/informacio_secundaria/export_secondary_info/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import secondary info from CSV */
  importSecondaryInfo(params: ImportSecondaryInfoParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/informacio_secundaria/import_secondary_info/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.InformacioSecundaria> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_informacio: params.codi_informacio,
    };
    return this.http.get<__model.InformacioSecundaria>(this.apiConfigService.options.apiUrl + `/informacio_secundaria/${pathParams.codi_informacio}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.InformacioSecundaria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_informacio: params.codi_informacio,
    };
    return this.http.put<__model.InformacioSecundaria>(this.apiConfigService.options.apiUrl + `/informacio_secundaria/${pathParams.codi_informacio}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.InformacioSecundaria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_informacio: params.codi_informacio,
    };
    return this.http.patch<__model.InformacioSecundaria>(this.apiConfigService.options.apiUrl + `/informacio_secundaria/${pathParams.codi_informacio}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      codi_informacio: params.codi_informacio,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/informacio_secundaria/${pathParams.codi_informacio}/`, {responseType: 'text'});
  }
}
